import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { SelectedSchemaCondition } from "types/campaigns";
import { campaignEffects } from "../../../campaignHelpers";
import { Effect, EffectError } from "./Effect";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    buttonAdd: {
      lineHeight: "24px",
      margin: theme.spacing(8, 8),
      padding: theme.spacing(2, 8),
    },
    conditionWrapper: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
    },
    error: {
      color: theme.red500,
      marginLeft: theme.spacing(8),
    },
    conditionHeader: {
      fontSize: 14,
      fontWeight: 500,
      margin: theme.spacing(8),
    },
  }),
);

interface Props {
  ruleIndex: number;
  selectedSchemaConditions?: SelectedSchemaCondition[];
  error?: {
    [key: number]: string;
  };
}

export const Effects = ({
  ruleIndex,
  selectedSchemaConditions,
  error,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();

  // Error for missing effects
  const isGeneralError =
    error && Array.isArray(error) && typeof error[0] === "string";

  return (
    <>
      <Typography className={classes.conditionHeader}>
        {message({ id: "effects" })}
        {isGeneralError && <span className={classes.error}>{error[0]}</span>}
      </Typography>
      <FieldArray
        name={`rules.[${ruleIndex}].effects`}
        render={({
          remove,
          insert,
          replace,
          unshift,
          form: { values, setFieldValue, setFieldError },
        }) => {
          const { rules, trigger, type } = values;
          const areEffectAdded = rules && rules[ruleIndex].effects?.length > 0;

          return (
            <>
              <OLMenuButton
                buttonVariant="outlined"
                className={classes.buttonAdd}
                size="small"
                startIcon={<PlusIcon />}
                data-test-id="effects-add"
                items={campaignEffects.map((item) => {
                  return {
                    label: message({ id: item.effect }),
                    onClick: () => {
                      if (isGeneralError) {
                        // Remove general error
                        setFieldError(
                          `rules.[${ruleIndex}].effects`,
                          undefined,
                        );
                      }
                      unshift({
                        draft: { effect: item.effect },
                        isEditing: true,
                        isSaved: false,
                        isChecked: false,
                      });
                    },
                  };
                })}
                placeholder={message({
                  id: "typeEffect",
                })}
                submit={message({
                  id: "addEffect",
                })}
              />

              {areEffectAdded && (
                <div className={classes.conditionWrapper}>
                  {rules?.[ruleIndex]?.effects?.map(
                    (
                      input: {
                        effect: string;
                        draft: any;
                        pointsRule: string;
                      },
                      index: number,
                    ) => {
                      const effectError = error && error[index];

                      return (
                        <Effect
                          key={index}
                          input={input.draft || input}
                          effect={rules[ruleIndex]?.effects[index]}
                          index={index}
                          ruleIndex={ruleIndex}
                          replace={replace}
                          trigger={trigger}
                          selectedSchemaConditions={selectedSchemaConditions}
                          setFieldValue={setFieldValue}
                          remove={remove}
                          insert={insert}
                          setFieldError={setFieldError}
                          error={effectError as EffectError}
                          type={type}
                        />
                      );
                    },
                  )}
                </div>
              )}
            </>
          );
        }}
      />
    </>
  );
};
